import type { ReactNode } from "react";
import { Disclosure } from "@headlessui/react";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { Icon } from "@circle-react-shared/Icon";
import { DefaultEditorV2Collapsable } from "./DefaultEditorV2/DefaultEditorV2Collapsable";

interface CollapsibleProps {
  children: ReactNode;
  defaultOpen?: boolean;
  noBorder?: boolean;
  noPadding?: boolean;
  title: ReactNode;
}

export const EditorCollapsable = ({
  title,
  children,
  noPadding,
  noBorder,
  defaultOpen = false,
}: CollapsibleProps) => {
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  if (isPropertiesPanelV2) {
    return (
      <DefaultEditorV2Collapsable title={title} defaultOpen={false}>
        {children}
      </DefaultEditorV2Collapsable>
    );
  }

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <div
          className={classNames("collapsible", {
            "collapsible--no-border": noBorder || !open,
          })}
        >
          <Disclosure.Button
            className={classNames(
              "collapsible__title border-primary items-center !px-6 !py-3",
              {
                "collapsible__title--no-padding": noPadding,
                "border-b": !open,
              },
            )}
          >
            <span className={classNames("text-xs font-semibold")}>{title}</span>
            <span className="flex size-[1rem] items-center justify-center">
              <Icon
                type="16-chevron-down"
                size={16}
                className={classNames("collapsible__arrow text-dark", {
                  "collapsible__arrow--open": open,
                })}
              />
            </span>
          </Disclosure.Button>
          <Disclosure.Panel className="collapsible__children" unmount={false}>
            {children}
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
};
